import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Property } from '../types';
import { MapPin, Bed, Car, Ruler } from 'lucide-react';
import { supabase } from '../integrations/supabase/client';
import { getPrimaryImageUrl, PropertyImage } from '../utils/imageUtils';

interface PropertyCardProps {
  property: Property;
}

const PropertyCard: React.FC<PropertyCardProps> = ({ property }) => {
  const [allImages, setAllImages] = useState<PropertyImage[]>(property.media.images || []);

  // Carregar imagens do condomínio se necessário
  useEffect(() => {
    const loadCondominiumImages = async () => {
      if (!property.condominiumId) {
        setAllImages(property.media.images || []);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('condominiums')
          .select('images')
          .eq('id', property.condominiumId)
          .single();

        if (error) {
          console.error('Erro ao buscar imagens do condomínio:', error);
          setAllImages(property.media.images || []);
          return;
        }

        const condominiumImages: PropertyImage[] = Array.isArray(data.images) 
          ? data.images
              .filter(img => img && typeof img === 'object' && 'url' in img)
              .map(img => img as PropertyImage)
          : [];
        const propertyImages = property.media.images || [];
        
        // Combinar: imagens do imóvel primeiro, depois do condomínio
        setAllImages([...propertyImages, ...condominiumImages]);
      } catch (error) {
        console.error('Erro ao carregar imagens do condomínio:', error);
        setAllImages(property.media.images || []);
      }
    };

    loadCondominiumImages();
  }, [property.condominiumId, property.media.images]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getPublicationColor = (type: string) => {
    switch (type) {
      case 'PREMIUM': return 'bg-orange-100 text-orange-800';
      case 'STANDARD': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Luxo': return 'bg-purple-100 text-purple-800';
      case 'Alto Padrão': return 'bg-blue-100 text-blue-800';
      case 'Médio/Alto': return 'bg-green-100 text-green-800';
      case 'Médio': return 'bg-yellow-100 text-yellow-800';
      case 'Econômico': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTransactionColor = (type: string) => {
    switch (type) {
      case 'Venda': return 'bg-green-100 text-green-800';
      case 'Aluguel': return 'bg-blue-100 text-blue-800';
      case 'Venda/Aluguel': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Usar a função utilitária para obter a imagem principal
  const primaryImageUrl = getPrimaryImageUrl(allImages);

  return (
    <Link to={`/imovel/${property.listingId}`} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 group block">
      <div className="relative">
        <img 
          src={primaryImageUrl} 
          alt={property.title}
          className="w-full h-40 sm:h-48 object-cover group-hover:scale-105 transition-transform duration-300"
        />
        {allImages.length > 1 && (
          <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
            {allImages.length} fotos
          </div>
        )}
        <div className="absolute top-2 sm:top-3 left-2 sm:left-3 flex flex-col sm:flex-row space-y-1 sm:space-y-0 sm:space-x-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTransactionColor(property.transactionType)}`}>
            {property.transactionType}
          </span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPublicationColor(property.publicationType)}`}>
            {property.publicationType}
          </span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(property.category)}`}>
            {property.category}
          </span>
        </div>
      </div>
      
      <div className="p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2 sm:mb-2">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 line-clamp-2 mb-1 sm:mb-0">
            {property.title}
          </h3>
          <span className="text-xs text-gray-500 sm:ml-2 flex-shrink-0">
            {property.propertyType}
          </span>
        </div>
        
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 space-y-2 sm:space-y-0">
          <div className="flex items-center text-gray-600 text-sm">
            <MapPin className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
            <span className="truncate">{property.location.neighborhood}, {property.location.city}</span>
          </div>
          <div className="text-xs font-mono bg-gray-100 text-gray-700 px-2 py-1 rounded self-start sm:self-auto">
            {property.propertyCode}
          </div>
        </div>
        
        <div className="flex items-center justify-between text-gray-600 text-sm mb-3">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="flex items-center">
              <Bed className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
              <span>{property.bedrooms}</span>
            </div>
            <div className="flex items-center">
              <Car className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
              <span>{property.garage.quantity}</span>
            </div>
            <div className="flex items-center">
              <Ruler className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
              <span>{property.livingArea}m²</span>
            </div>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div className="flex flex-col">
            <div className="text-xl sm:text-2xl font-bold text-orange-600 mb-1">
              {(() => {
                // Para aluguel, mostrar rental_price
                if (property.transactionType === 'Aluguel') {
                  return formatPrice(property.rentalPrice || 0);
                }
                
                // Para venda, mostrar list_price  
                if (property.transactionType === 'Venda') {
                  return formatPrice(property.listPrice);
                }
                
                // Para venda/aluguel, mostrar ambos
                if (property.transactionType === 'Venda/Aluguel') {
                  return (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-1 sm:space-y-0">
                      <span className="text-sm sm:text-base font-bold text-green-600">Venda: {formatPrice(property.listPrice)}</span>
                      <span className="text-sm sm:text-base font-bold text-blue-600">Locação: {formatPrice(property.rentalPrice || 0)}</span>
                    </div>
                  );
                }
                
                // Fallback
                return formatPrice(property.listPrice);
              })()}
            </div>
            
            {/* Condomínio e IPTU */}
            <div className="flex flex-col space-y-1 text-xs text-gray-600">
              {property.propertyAdministrationFee && property.propertyAdministrationFee > 0 && (
                <span>Condomínio: {formatPrice(property.propertyAdministrationFee)}</span>
              )}
              {property.yearlyTax && property.yearlyTax > 0 && (
                <span>IPTU: {formatPrice(property.yearlyTax)}/mês</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default PropertyCard;